import React from 'react';
import { cn } from '@/lib/utils';
import { X, FileText } from 'lucide-react';

interface TabProps {
  id: string;
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  isActive: boolean;
  isSplitView?: boolean;
  isPanelFocused?: boolean; // Whether this tab's panel is the focused one
  canClose?: boolean;
  draggable?: boolean;
  draggedIndex?: number | null;
  dragOverIndex?: number | null;
  actualIndex?: number;
  onDragStart?: (e: React.DragEvent, index: number) => void;
  onDragOver?: (e: React.DragEvent, index: number) => void;
  onDragLeave?: () => void;
  onDrop?: (e: React.DragEvent, index: number) => void;
  onDragEnd?: () => void;
  onClick?: () => void;
  onClose?: () => void;
  className?: string;
}

export const Tab: React.FC<TabProps> = ({
  id,
  title,
  icon: Icon,
  isActive,
  isSplitView = false,
  isPanelFocused = true,
  canClose = true,
  draggable = false,
  draggedIndex,
  dragOverIndex,
  actualIndex,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onClick,
  onClose,
  className
}) => {
  // Handle middle mouse button click to close tab
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 && canClose && onClose) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div
      key={id}
      draggable={draggable}
      onDragStart={onDragStart && actualIndex !== undefined ? (e) => onDragStart(e, actualIndex) : undefined}
      onDragOver={onDragOver && actualIndex !== undefined ? (e) => onDragOver(e, actualIndex) : undefined}
      onDragLeave={onDragLeave}
      onDrop={onDrop && actualIndex !== undefined ? (e) => onDrop(e, actualIndex) : undefined}
      onDragEnd={onDragEnd}
      onMouseDown={handleMouseDown}
      className={cn(
        "flex items-center min-w-16 w-fit max-w-64 px-4 cursor-pointer rounded-t-sm",
        "transition-colors duration-150",
        "group relative",
        
        isActive 
          ? "bg-surface border-l border-r border-b-0 h-10 -mb-1 z-20" 
          : "h-8 hover:bg-grey-G7",
        // Show focused indicator: thicker top border when panel is focused
        isActive && isPanelFocused 
          ? "border-t-2 border-t-primary" 
          : isActive && "border-t border-t-border",
        draggedIndex === actualIndex && "opacity-50 scale-95",
        dragOverIndex === actualIndex && "border-primary border-2",
        className
      )}
      style={isActive ? { transform: 'translateY(1px)' } : undefined}
      onClick={onClick}
    >
      {/* Bottom extension for active tab to cover TabBar border */}
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-surface translate-y-full z-30" />
      )}
      {/* Page icon */}
      <div className="ml-2 mr-2 flex-shrink-0">
        {Icon ? (
          <Icon className={cn(
            "text-muted-foreground",
            isSplitView ? "h-3 w-3" : "h-4 w-4"
          )} />
        ) : (
          <FileText className={cn(
            "text-muted-foreground",
            isSplitView ? "h-3 w-3" : "h-4 w-4"
          )} />
        )}
      </div>
      
      <span 
        className="truncate text-sm font-medium min-w-0 flex-1"
        title={title}
      >
        {title}{isSplitView && " (Split)"}
      </span>
      
      {/* Close button */}
      {canClose && onClose && (
        <button
          className={cn(
            "ml-2 p-1 rounded-sm transition-colors",
            "flex-shrink-0 opacity-0 group-hover:opacity-100",
            isSplitView 
              ? "hover:bg-orange-500/20" 
              : "hover:bg-accent-foreground/10"
          )}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          title={isSplitView ? "Close split view" : "Close tab"}
        >
          <X className={cn(
            "h-3 w-3",
            isSplitView && "text-orange-500"
          )} />
        </button>
      )}
    </div>
  );
};
